import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareHeart, Star, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [whatWorked, setWhatWorked] = useState("");
  const [whatConfusing, setWhatConfusing] = useState("");
  const [nextFeature, setNextFeature] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error("Pick a star rating first"); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("ws_token");
      await axios.post(
        `${API}/api/feedback`,
        {
          rating,
          feedback_type: "general",
          what_worked: whatWorked,
          what_confusing: whatConfusing,
          next_feature: nextFeature,
          comment: [whatWorked, whatConfusing, nextFeature].filter(Boolean).join(" | "),
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      toast.success("Thanks! We read every word.");
      setOpen(false);
      setRating(0); setWhatWorked(""); setWhatConfusing(""); setNextFeature("");
    } catch { toast.error("Couldn't send — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="feedback-launcher"
        onClick={() => setOpen(true)}
        aria-label="Give feedback"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-full pl-4 pr-5 py-3 shadow-lg"
        style={{ boxShadow: "0 8px 24px -4px rgba(91, 33, 182, 0.5)" }}
      >
        <MessageSquareHeart size={18} />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div data-testid="feedback-modal" className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border-2 border-slate-100 dark:border-slate-800">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Help us make WriterSpark better</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">We read every response.</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="feedback-close" aria-label="Close" className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500 dark:text-slate-400">Rate your experience</label>
              <div className="flex gap-1 mt-2" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button"
                    data-testid={`star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`Rate ${n} stars`}>
                    <Star size={28} className={(hover || rating) >= n ? "text-amber-400 fill-amber-400" : "text-slate-300 dark:text-slate-600"} />
                  </button>
                ))}
              </div>
            </div>

            <FbField label="What worked well?" placeholder="The essay coach feedback was clearer than I expected..." value={whatWorked} onChange={setWhatWorked} testId="fb-worked" />
            <FbField label="What was confusing?" placeholder="Couldn't figure out how to..." value={whatConfusing} onChange={setWhatConfusing} testId="fb-confusing" />
            <FbField label="What feature would you like next?" placeholder="I'd love to see..." value={nextFeature} onChange={setNextFeature} testId="fb-next" />

            <button onClick={submit} disabled={busy} data-testid="feedback-submit"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full py-3 disabled:opacity-60">
              <Send size={14} /> {busy ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const FbField = ({ label, placeholder, value, onChange, testId }) => (
  <label className="block mb-3">
    <span className="text-xs uppercase font-bold tracking-widest text-slate-500 dark:text-slate-400">{label}</span>
    <textarea
      data-testid={testId}
      rows={2}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-violet-400 px-3 py-2 outline-none text-sm text-slate-900 dark:text-slate-100"
    />
  </label>
);
